// src/pages/Dashboard.jsx - COMPLETE WITH REQUEST/RESPONSE/CANCEL FLOW
import React, { useState, useEffect } from 'react';
import { Search, MapPin, Calendar, Users, Car, Plus, Filter, Star, Shield, Bell, LogOut, Send, Repeat, Phone, PhoneOff, AlertTriangle, GraduationCap, Wind, ThermometerSun, Baby, UserCheck, Settings, Award, Trophy, Edit, X, User, Home, Briefcase, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import * as api from '../services/api';
import LocationAutocomplete from '../components/LocationAutocomplete';
import SavedLocations from '../components/SavedLocations';
import { loadGoogleMapsScript } from '../utils/loadGoogleMaps';

const JourniLogo = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 200 200" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M120 30 L120 120 Q120 150 90 150 L70 150" fill="#10b981" />
    <path d="M140 30 L160 30 Q170 30 170 40 L170 80" fill="#10b981" />
    <path d="M50 130 L70 150 L70 170 Q70 180 60 180 L40 180 Q30 180 30 170 L30 130 Z" fill="#10b981" />
    <circle cx="90" cy="130" r="20" fill="white" />
    <circle cx="90" cy="130" r="12" fill="#10b981" />
  </svg>
);

const PreferenceBadge = ({ type }) => {
  const badges = {
    nonSmoking: { icon: Wind, text: "Non-Smoking", color: "bg-blue-100 text-blue-700" },
    managedClimate: { icon: ThermometerSun, text: "AC", color: "bg-cyan-100 text-cyan-700" },
    childFriendly: { icon: Baby, text: "Child Friendly", color: "bg-amber-100 text-amber-700" },
    femaleOnly: { icon: UserCheck, text: "Female Only", color: "bg-pink-100 text-pink-700" }
  };

  const badge = badges[type];
  if (!badge) return null;
  const Icon = badge.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
      <Icon className="w-3 h-3" />
      {badge.text}
    </span>
  );
};

const AchievementBadge = ({ type }) => {
  const achievements = {
    femaleFriendly: { icon: Shield, text: "Female Friendly", color: "bg-purple-100 text-purple-700 border-purple-300" },
    childFriendlyAch: { icon: Baby, text: "Family Choice", color: "bg-orange-100 text-orange-700 border-orange-300" },
    rookie: { icon: Award, text: "Rookie", color: "bg-gray-100 text-gray-700 border-gray-300" },
    expert: { icon: Trophy, text: "Expert", color: "bg-gray-200 text-gray-700 border-gray-400" },
    pro: { icon: Trophy, text: "Pro", color: "bg-yellow-100 text-yellow-800 border-yellow-400" },
    elite: { icon: Trophy, text: "Elite", color: "bg-purple-100 text-purple-800 border-purple-400" },
    topRated: { icon: Star, text: "5-Star", color: "bg-yellow-100 text-yellow-700 border-yellow-300" }
  };

  const achievement = achievements[type];
  if (!achievement) return null;
  const Icon = achievement.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${achievement.color}`}>
      <Icon className="w-3 h-3" />
      {achievement.text}
    </span>
  );
};

export default function JourniDashboard() {
  const [activeView, setActiveView] = useState('find');
  const [showFilters, setShowFilters] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showFamilyProfiles, setShowFamilyProfiles] = useState(false);
  const [showSavedLocations, setShowSavedLocations] = useState(false);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);

  const [ridePreferences, setRidePreferences] = useState({
    nonSmoking: false,
    femaleOnly: false,
    managedClimate: false,
    childFriendly: false
  });

  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState([]);

  const [userProfile, setUserProfile] = useState({
    prefersNonSmoking: true,
    prefersManagedClimate: true,
    prefersChildFriendly: false,
    shareContact: 'none',
    achievements: ['femaleFriendly', 'expert', 'topRated'],
    totalRides: 287,
    rating: 4.9
  });

  const [familyProfiles, setFamilyProfiles] = useState([
    { id: 1, name: 'Me', type: 'self', prefersNonSmoking: true, prefersFemaleOnly: false },
    { id: 2, name: 'Mom', type: 'parent', prefersNonSmoking: true, prefersFemaleOnly: true },
    { id: 3, name: 'Dad', type: 'parent', prefersNonSmoking: false, prefersFemaleOnly: false },
    { id: 4, name: 'Sarah', type: 'child', age: 10, school: 'Green Valley School' },
    { id: 5, name: 'Ahmed', type: 'child', age: 8, school: 'Green Valley School' }
  ]);

  const [selectedTravelers, setSelectedTravelers] = useState([1]);

  // Form States
  const [rideForm, setRideForm] = useState({
    startAddress: '',
    endAddress: '',
    startLat: null,
    startLng: null,
    endLat: null,
    endLng: null,
    date: '',
    time: '',
    seats_available: 1,
    price_per_seat: '',
    notes: ''
  });
  
  const [searchForm, setSearchForm] = useState({
    start: { text: '', lat: null, lng: null },
    end: { text: '', lat: null, lng: null },
    date: ''
  });

  const [isPosting, setIsPosting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [myRides, setMyRides] = useState({ as_driver: [], as_rider: [] });
  const [loadingMyRides, setLoadingMyRides] = useState(false);
  const [driverRequests, setDriverRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  
  // Recommended rides state
  const [recommendedRides, setRecommendedRides] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);

  // Response/Cancel modal state
  const [responseReason, setResponseReason] = useState('');

  // Quick connectivity test (runs once on page load)
  useEffect(() => {
    console.log("API BASE:", process.env.REACT_APP_API_BASE_URL);
    loadMyRides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Google Maps
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (apiKey) {
      loadGoogleMapsScript(apiKey)
        .then(() => console.log('Google Maps loaded'))
        .catch(err => console.error('Failed to load Google Maps:', err));
    }
  }, []);

  // Load user's rides when switching to My Rides view
  useEffect(() => {
    if (activeView === 'my-rides') {
      loadMyRides();
      loadDriverRequests();
    }
  }, [activeView]);

  // Load recommended rides when switching to Find Rides view
  useEffect(() => {
    if (activeView === 'find') {
      loadRecommendedRides();
    }
  }, [activeView]);

  const loadMyRides = async () => {
    setLoadingMyRides(true);
    try {
      const response = await api.getMyRides();
      console.log('My rides response:', response);
      
      if (response.success) {
        setMyRides(response.rides || { as_driver: [], as_rider: [] });
      }
    } catch (error) {
      console.error('Failed to load rides:', error);
    } finally {
      setLoadingMyRides(false);
    }
  };

  const loadDriverRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await api.getDriverRequests();
      if (response.success) {
        setDriverRequests(response.requests || []);
      }
    } catch (error) {
      console.error('Failed to load driver requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const loadRecommendedRides = async () => {
    setLoadingRecommended(true);
    try {
      const response = await api.getRecommendedRides();
      if (response.success) {
        setRecommendedRides(response.rides || []);
        console.log(`Loaded ${response.count || 0} recommended rides`);
      }
    } catch (error) {
      console.error('Failed to load recommended rides:', error);
    } finally {
      setLoadingRecommended(false);
    }
  };

  const handlePostRide = async () => {
    if (!rideForm.startAddress || !rideForm.endAddress || !rideForm.date || !rideForm.time) {
      alert('Please fill in all required fields');
      return;
    }

    if (!rideForm.startLat || !rideForm.endLat) {
      alert('Please select locations from the dropdown suggestions');
      return;
    }
    
    if (!rideForm.price_per_seat || parseFloat(rideForm.price_per_seat) <= 0) {
      alert('Please enter a valid price');
      return;
    }

    setIsPosting(true);
    try {
      const rideData = {
        start_text: rideForm.startAddress,
        end_text: rideForm.endAddress,
        start_lat: rideForm.startLat,
        start_lng: rideForm.startLng,
        end_lat: rideForm.endLat,
        end_lng: rideForm.endLng,
        depart_at: `${rideForm.date} ${rideForm.time}:00`,
        seats_available: parseInt(rideForm.seats_available),
        price_per_seat: parseFloat(rideForm.price_per_seat),
        female_only: ridePreferences.femaleOnly ? 1 : 0,
        non_smoking: (ridePreferences.nonSmoking || userProfile.prefersNonSmoking) ? 1 : 0,
        child_friendly: (ridePreferences.childFriendly || userProfile.prefersChildFriendly) ? 1 : 0,
        managed_climate: (ridePreferences.managedClimate || userProfile.prefersManagedClimate) ? 1 : 0,
        urgency: 'scheduled'
      };
      
      const response = await api.createRide(rideData);

      if (response.success) {
        alert('Ride posted successfully!');
        setRideForm({
          startAddress: '',
          endAddress: '',
          startLat: null,
          startLng: null,
          endLat: null,
          endLng: null,
          date: '',
          time: '',
          seats_available: 1,
          price_per_seat: '',
          notes: ''
        });
        setIsRecurring(false);
        setRecurringDays([]);
        setRidePreferences({ nonSmoking: false, femaleOnly: false, managedClimate: false, childFriendly: false });
        setActiveView('my-rides');
      } else {
        alert('Error: ' + (response.message || 'Failed to post ride'));
      }
    } catch (error) {
      console.error('Post ride error:', error);
      alert('Failed to post ride: ' + (error.response?.data?.error || error.response?.data?.message || error.message));
    } finally {
      setIsPosting(false);
    }
  };
    
    const handleSearchRides = async () => {
  if (!searchForm.start.text || !searchForm.end.text) {
    alert('Please enter both pickup and drop-off locations');
    return;
  }

  if (!searchForm.start.lat || !searchForm.end.lat) {
    alert('Please select locations from the dropdown suggestions');
    return;
  }
  
  setIsSearching(true);
  try {
    const searchParams = {
      lat: searchForm.start.lat,
      lng: searchForm.start.lng
    };
    
    if (searchForm.date) {
      searchParams.depart_after = `${searchForm.date} 00:00:00`;
    }
    
    const response = await api.searchRides(searchParams);

    if (response.success) {
      const rides = response.rides || [];
      setSearchResults(rides);
    } else {
      alert('Search failed: ' + (response.message || 'Unknown error'));
      setSearchResults([]);
    }
  } catch (error) {
    console.error('Search error:', error);
    alert('Failed to search rides: ' + (error.response?.data?.message || error.message));
    setSearchResults([]);
  } finally {
    setIsSearching(false);
  }
};
      
  const handleRequestRide = async (rideId) => {
    try {
      const response = await api.requestRide(rideId);

      if (response.success) {
        alert('Ride requested successfully! The driver will be notified.');
        setSearchResults(prev => prev.filter(r => r.id !== rideId));
        setRecommendedRides(prev => prev.filter(r => r.id !== rideId));
      } else {
        alert('Request failed: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Request error:', error);
      alert('Failed to request ride: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRespondToRequest = async (action) => {
    if (!currentAction) return;

    if (action === 'decline' && !responseReason.trim()) {
      alert('Please provide a reason for declining');
      return;
    }

    try {
      const response = await api.respondToRideRequest(
        currentAction.rideId,
        currentAction.riderId,
        action,
        responseReason || null
      );

      if (response.success) {
        alert(action === 'accept' ? 'Request accepted!' : 'Request declined');
        setShowRespondModal(false);
        setCurrentAction(null);
        setResponseReason('');
        loadDriverRequests();
        loadMyRides();
      } else {
        alert('Failed: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Response error:', error);
      alert('Failed to respond: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancelRide = async () => {
    if (!currentAction) return;

    try {
      const response = await api.cancelRide(
        currentAction.rideId,
        currentAction.role,
        responseReason || null
      );

      if (response.success) {
        alert('Ride cancelled successfully');
        setShowCancelModal(false);
        setCurrentAction(null);
        setResponseReason('');
        loadMyRides();
        if (currentAction.role === 'driver') {
          loadDriverRequests();
        }
      } else {
        alert('Failed to cancel: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Cancel error:', error);
      alert('Failed to cancel ride: ' + (error.response?.data?.message || error.message));
    }
  };

  const openRespondModal = (action, rideId, riderId) => {
    setCurrentAction({ action, rideId, riderId });
    setResponseReason('');
    setShowRespondModal(true);
  };

  const openCancelModal = (rideId, role) => {
    setCurrentAction({ rideId, role });
    setResponseReason('');
    setShowCancelModal(true);
  };

  const toggleRecurringDay = (day) => {
    setRecurringDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleRidePreference = (pref) => {
    setRidePreferences(prev => ({ ...prev, [pref]: !prev[pref] }));
  };

  const toggleTraveler = (id) => {
    setSelectedTravelers(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const currentUser = api.getCurrentUser();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <JourniLogo className="w-9 h-9" />
              <h1 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Journi</h1>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-gray-100 rounded-lg relative">
                <Bell className="w-5 h-5 text-gray-600" />
                {driverRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
              <button
                onClick={() => setShowProfileSettings(!showProfileSettings)}
                className="p-2 hover:bg-gray-100 rounded-lg">
                <Settings className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <LogOut className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-9 h-9 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                {currentUser?.name?.substring(0, 2).toUpperCase() || 'JD'}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setActiveView('find')}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition ${activeView === 'find' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Search className="w-4 h-4 inline mr-1.5" />
              Find Rides
            </button>
            <button
              onClick={() => setActiveView('offer')}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition ${activeView === 'offer' ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}>
              <Plus className="w-4 h-4 inline mr-1.5" />
              Offer Ride
            </button>
            <button
              onClick={() => setActiveView('skoolpool-request')}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition ${activeView.startsWith('skoolpool') ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}>
              <GraduationCap className="w-4 h-4 inline mr-1.5" />
              SkoolPool
            </button>
            <button
              onClick={() => setActiveView('my-rides')}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition relative ${activeView === 'my-rides' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Car className="w-4 h-4 inline mr-1.5" />
              My Rides
              {driverRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {driverRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* RESPOND MODAL */}
      {showRespondModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">
              {currentAction?.action === 'accept' ? 'Accept Request' : 'Decline Request'}
            </h3>
            
            {currentAction?.action === 'decline' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for declining (required)
                </label>
                <textarea
                  value={responseReason}
                  onChange={(e) => setResponseReason(e.target.value)}
                  placeholder="e.g., Ride is full, Different route, etc."
                  className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-emerald-500"
                  rows={3}
                />
              </div>
            )}

            {currentAction?.action === 'accept' && (
              <p className="text-sm text-gray-600 mb-4">
                By accepting, you confirm this rider can join your ride. Your contact info will be shared.
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => handleRespondToRequest(currentAction?.action)}
                className={`flex-1 py-3 rounded-lg font-medium ${
                  currentAction?.action === 'accept'
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}>
                Confirm
              </button>
              <button
                onClick={() => {
                  setShowRespondModal(false);
                  setCurrentAction(null);
                  setResponseReason('');
                }}
                className="flex-1 py-3 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL MODAL */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Cancel Ride</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for cancelling (optional)
              </label>
              <textarea
                value={responseReason}
                onChange={(e) => setResponseReason(e.target.value)}
                placeholder="e.g., Change of plans, Emergency, etc."
                className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-red-500"
                rows={3}
              />
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
              <p className="text-sm text-yellow-800">
                ⚠️ {currentAction?.role === 'driver' 
                  ? 'All riders will be notified of the cancellation.' 
                  : 'The driver will be notified of your cancellation.'}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCancelRide}
                className="flex-1 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium">
                Cancel Ride
              </button>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCurrentAction(null);
                  setResponseReason('');
                }}
                className="flex-1 py-3 border rounded-lg hover:bg-gray-50">
                Keep Ride
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SAVED LOCATIONS MODAL */}
      {showSavedLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <SavedLocations onClose={() => {
              setShowSavedLocations(false);
              loadRecommendedRides();
            }} />
            <button
              onClick={() => {
                setShowSavedLocations(false);
                loadRecommendedRides();
              }}
              className="w-full mt-4 px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200">
              Close
            </button>
          </div>
        </div>
      )}

      {/* PROFILE SETTINGS MODAL - Keep existing code */}
      {showProfileSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            {/* Keep all existing profile settings code */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Profile & Settings</h2>
              <button onClick={() => setShowProfileSettings(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {currentUser?.name?.substring(0, 2).toUpperCase() || 'JD'}
                </div>
                <div>
                  <h3 className="font-bold text-lg">{currentUser?.name || 'User'}</h3>
                  <div className="text-sm text-gray-600">{currentUser?.phone || ''}</div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span>{userProfile.rating} rating</span>
                    <span>•</span>
                    <span>{userProfile.totalRides} rides</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-600" />
                Your Achievements
              </h3>
              <div className="flex flex-wrap gap-2">
                {userProfile.achievements.map(ach => (
                  <AchievementBadge key={ach} type={ach} />
                ))}
              </div>
            </div>

            <div className="mb-6">
              <button
                onClick={() => {
                  setShowProfileSettings(false);
                  setShowSavedLocations(true);
                }}
                className="w-full p-4 border-2 border-emerald-200 rounded-lg hover:bg-emerald-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  <div className="text-left">
                    <div className="font-medium">Saved Locations</div>
                    <div className="text-sm text-gray-600">Get automatic ride recommendations</div>
                  </div>
                </div>
                <span className="text-emerald-600 text-xl">→</span>
              </button>
            </div>

            <button
              onClick={() => setShowProfileSettings(false)}
              className="w-full px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 font-medium">
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* FIND RIDES VIEW - Keep existing code with recommended rides */}
      {activeView === 'find' && (
        <>
          {recommendedRides.length > 0 && (
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b-2 border-emerald-100">
              <div className="max-w-7xl mx-auto px-4 py-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                      Rides Near Your Saved Locations
                    </h2>
                    <p className="text-sm text-gray-600">
                      {recommendedRides.length} rides within 3.5km (next 14 days)
                    </p>
                  </div>
                  <button
                    onClick={() => setShowSavedLocations(true)}
                    className="px-4 py-2 border border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 text-sm">
                    Manage Locations
                  </button>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recommendedRides.slice(0, 4).map(ride => (
                    <div key={ride.id} className="bg-white rounded-lg border-2 border-emerald-200 p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex gap-2 mb-2">
                            <MapPin className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm font-medium line-clamp-1">{ride.origin}</span>
                          </div>
                          <div className="flex gap-2">
                            <MapPin className="w-4 h-4 text-red-500 fill-red-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm font-medium line-clamp-1">{ride.destination}</span>
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          <div className="text-lg font-bold text-emerald-600">Rs. {ride.price_per_seat}</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-3 border-t">
                        <div className="text-xs text-gray-600">
                          {new Date(ride.time).toLocaleDateString()}
                        </div>
                        <button
                          onClick={() => handleRequestRide(ride.id)}
                          className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm">
                          Request
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {recommendedRides.length > 4 && (
                  <div className="text-center mt-4">
                    <p className="text-sm text-gray-600">
                      And {recommendedRides.length - 4} more rides near your locations!
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rest of Find Rides view - keep existing search form and results */}
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 py-4">
              <div className="mb-4 p-4 bg-gray-100 rounded-lg opacity-60">
                <label className="block text-sm font-medium text-gray-500 mb-2">Who's traveling? (Coming Soon)</label>
                <div className="flex flex-wrap gap-2">
                  {familyProfiles.map(profile => (
                    <button
                      key={profile.id}
                      disabled
                      className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-400 cursor-not-allowed">
                      {profile.name}
                      {profile.type === 'child' && ' 👶'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3 mb-3">
                <LocationAutocomplete
                  value={searchForm.start.text}
                  onChange={(value) => setSearchForm({ ...searchForm, start: { ...searchForm.start, text: value } })}
                  onPlaceSelect={(location) => setSearchForm({
                    ...searchForm,
                    start: { text: location.address, lat: location.lat, lng: location.lng }
                  })}
                  placeholder="Pickup location"
                  icon="pickup"
                />
                
                <LocationAutocomplete
                  value={searchForm.end.text}
                  onChange={(value) => setSearchForm({ ...searchForm, end: { ...searchForm.end, text: value } })}
                  onPlaceSelect={(location) => setSearchForm({
                    ...searchForm,
                    end: { text: location.address, lat: location.lat, lng: location.lng }
                  })}
                  placeholder="Drop-off location"
                  icon="dropoff"
                />
                
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={searchForm.date}
                    onChange={(e) => setSearchForm({ ...searchForm, date: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSearchRides}
                  disabled={isSearching}
                  className="flex-1 bg-emerald-500 text-white py-3 rounded-lg hover:bg-emerald-600 font-medium disabled:opacity-50">
                  <Search className="w-4 h-4 inline mr-2" />
                  {isSearching ? 'Searching...' : 'Search Rides'}
                </button>
                <button onClick={() => setShowFilters(!showFilters)} className="px-5 border rounded-lg hover:bg-gray-50">
                  <Filter className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-5">
            <h2 className="text-lg font-semibold mb-4">Search Results ({searchResults.length})</h2>
            {isSearching ? (
  <div className="text-center py-12 text-gray-500">
    <Car className="w-12 h-12 text-gray-300 mx-auto mb-3 animate-pulse" />
    Searching for available rides...
  </div>
) : searchResults.length > 0 ? (
  <>
    <p className="text-sm text-gray-600 mb-4">
      Found {searchResults.length} ride{searchResults.length > 1 ? 's' : ''} within 3km
    </p>
    {searchResults.map(ride => (
      <div key={ride.id} className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-5 hover:border-emerald-500 hover:shadow-md transition cursor-pointer mb-4">
        <div className="flex justify-between mb-3">
          <div className="flex gap-3">
            <div className="relative">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold text-sm">DR</div>
              <Shield className="absolute -bottom-1 -right-1 w-5 h-5 text-emerald-500 fill-emerald-500" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Driver</h3>
              <div className="flex gap-2 text-xs text-gray-600">
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  4.8
                </span>
                <span>120 rides</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">Rs. {ride.price_per_seat}</div>
            <div className="text-xs text-gray-500">per seat</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {ride.non_smoking === 1 && <PreferenceBadge type="nonSmoking" />}
          {ride.managed_climate === 1 && <PreferenceBadge type="managedClimate" />}
          {ride.child_friendly === 1 && <PreferenceBadge type="childFriendly" />}
          {ride.female_only === 1 && <PreferenceBadge type="femaleOnly" />}
        </div>

        <div className="flex gap-2 mb-3">
          <div className="flex flex-col items-center">
            <MapPin className="w-4 h-4 text-emerald-500" />
            <div className="w-px h-5 bg-gray-300"></div>
            <MapPin className="w-4 h-4 text-red-500 fill-red-500" />
          </div>
          <div className="flex-1 space-y-1.5">
            <div className="font-medium text-sm">{ride.start_text}</div>
            <div className="font-medium text-sm">{ride.end_text}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-600">{new Date(ride.depart_at).toLocaleString()}</div>
          </div>
        </div>
        
        <div className="flex justify-between items-center pt-3 border-t">
          <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded font-medium">
            {ride.seats_available} seat(s) available
          </span>
          <button
            onClick={() => handleRequestRide(ride.id)}
            className="bg-emerald-500 text-white px-6 py-2 rounded-lg hover:bg-emerald-600 font-medium text-sm">
            Request →
          </button>
        </div>
      </div>
    ))}
  </>
) : (
  /* NO RIDES FOUND CARD */
  <div className="text-center py-8">
    <div className="max-w-md mx-auto">
      <div className="mb-6">
        <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          No Rides Found
        </h3>
        <p className="text-gray-500">
          No available rides within 3km of your pickup location
        </p>
      </div>

      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200 p-6 text-left">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Send className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-emerald-900 text-lg mb-1">
              Post a Ride Request Instead
            </h4>
            <p className="text-sm text-gray-700">
              Let drivers know you're looking for a ride! Nearby drivers will be notified and can offer to pick you up.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 mb-4 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-emerald-500" />
            <span className="font-medium">From:</span>
            <span className="text-gray-600">{searchForm.start.text}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-red-500 fill-red-500" />
            <span className="font-medium">To:</span>
            <span className="text-gray-600">{searchForm.end.text}</span>
          </div>
          {searchForm.date && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="font-medium">Date:</span>
              <span className="text-gray-600">{new Date(searchForm.date).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <button
          onClick={() => alert('Coming soon!')}
          disabled={isPosting}
          className="w-full bg-emerald-500 text-white py-3 rounded-lg hover:bg-emerald-600 font-semibold text-base disabled:opacity-50 flex items-center justify-center gap-2">
          {isPosting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Posting Request...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Post My Ride Request
            </>
          )}
        </button>

        <p className="text-xs text-gray-600 mt-3 text-center">
          ✓ Drivers within 10km will be notified • ✓ You'll be alerted when someone accepts
        </p>
      </div>

      <button
        onClick={() => setActiveView('offer')}
        className="mt-4 text-sm text-gray-600 hover:text-gray-800">
        Or offer your own ride instead →
      </button>
    </div>
  </div>
)}
</div>
        </>
      )}

      {/* OFFER RIDE VIEW - Keep existing code */}
      {activeView === 'offer' && (
        <div className="max-w-3xl mx-auto px-4 py-6">
          {/* Keep all existing Offer Ride form code */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border-2 border-orange-200 p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Post Your Ride</h2>
            <p className="text-gray-600">Share your journey and earn while helping others commute</p>
          </div>

          <div className="bg-white rounded-xl border p-6 space-y-4">
            {/* Keep all existing form fields */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                <LocationAutocomplete
                  value={rideForm.startAddress}
                  onChange={(value) => setRideForm({ ...rideForm, startAddress: value })}
                  onPlaceSelect={(location) => setRideForm({
                    ...rideForm,
                    startAddress: location.address,
                    startLat: location.lat,
                    startLng: location.lng
                  })}
                  placeholder="e.g. F-6 Markaz, Islamabad"
                  icon="pickup"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                <LocationAutocomplete
                  value={rideForm.endAddress}
                  onChange={(value) => setRideForm({ ...rideForm, endAddress: value })}
                  onPlaceSelect={(location) => setRideForm({
                    ...rideForm,
                    endAddress: location.address,
                    endLat: location.lat,
                    endLng: location.lng
                  })}
                  placeholder="e.g. Blue Area, Islamabad"
                  icon="dropoff"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={rideForm.date}
                  onChange={(e) => setRideForm({ ...rideForm, date: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                <input
                  type="time"
                  value={rideForm.time}
                  onChange={(e) => setRideForm({ ...rideForm, time: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Seats</label>
                <select
                  value={rideForm.seats_available}
                  onChange={(e) => setRideForm({ ...rideForm, seats_available: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg bg-white focus:ring-2 focus:ring-emerald-500">
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price per Seat (PKR)</label>
                <input
                  type="number"
                  placeholder="250"
                  value={rideForm.price_per_seat}
                  onChange={(e) => setRideForm({ ...rideForm, price_per_seat: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>

            <button
              onClick={handlePostRide}
              disabled={isPosting}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white py-4 rounded-lg hover:shadow-lg transition font-semibold text-lg disabled:opacity-50">
              <Send className="w-5 h-5 inline mr-2" />
              {isPosting ? 'Posting...' : 'Post Ride'}
            </button>
          </div>
        </div>
      )}

        {/* MY RIDES VIEW */}
{activeView === 'my-rides' && (
  <div className="max-w-7xl mx-auto px-4 py-5">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold">
        My Rides ({(myRides.as_driver?.length || 0) + (myRides.as_rider?.length || 0)})
      </h2>
      <button 
        onClick={() => {
          loadMyRides();
          loadDriverRequests();
        }}
        disabled={loadingMyRides || loadingRequests}
        className="text-sm text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50">
        {(loadingMyRides || loadingRequests) ? 'Loading...' : '↻ Refresh'}
      </button>
    </div>
    
    {loadingMyRides ? (
      <div className="text-center py-12 text-gray-500">Loading your rides...</div>
    ) : (myRides.as_driver?.length === 0 && myRides.as_rider?.length === 0) ? (
      <div className="text-center py-12">
        <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 mb-2">No rides yet</p>
        <p className="text-sm text-gray-400 mb-4">Start by posting or requesting a ride</p>
        <div className="flex gap-3 justify-center">
          <button 
            onClick={() => setActiveView('offer')}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
            Offer a Ride
          </button>
          <button 
            onClick={() => setActiveView('find')}
            className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
            Find a Ride
          </button>
        </div>
      </div>
    ) : (
      <div className="space-y-6">
        {/* RIDES I'M OFFERING */}
        {myRides.as_driver?.length > 0 && (
          <div>
            <h3 className="text-md font-semibold mb-3 text-orange-700">
              Rides I'm Offering ({myRides.as_driver.length})
            </h3>
            <div className="space-y-3">
              {myRides.as_driver.map(ride => {
                const rideRequests = driverRequests.filter(req => req.ride_id === ride.id);
                const pendingCount = rideRequests.filter(req => req.status === 'pending').length;
                
                return (
                  <div key={ride.id} className="bg-white rounded-xl border-2 border-orange-200 p-5">
                    <div className="flex justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded text-xs font-medium">
                            Offering
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            ride.status === 'open' ? 'bg-green-100 text-green-700' :
                            ride.status === 'active' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {(ride.status || 'OPEN').toUpperCase()}
                          </span>
                          {pendingCount > 0 && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                              {pendingCount} New Request{pendingCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {ride.non_smoking === 1 && <PreferenceBadge type="nonSmoking" />}
                          {ride.managed_climate === 1 && <PreferenceBadge type="managedClimate" />}
                          {ride.child_friendly === 1 && <PreferenceBadge type="childFriendly" />}
                          {ride.female_only === 1 && <PreferenceBadge type="femaleOnly" />}
                        </div>

                        <div className="flex gap-2 mb-2">
                          <div className="flex flex-col items-center pt-1">
                            <MapPin className="w-4 h-4 text-emerald-500" />
                            <div className="w-px h-4 bg-gray-300"></div>
                            <MapPin className="w-4 h-4 text-red-500 fill-red-500" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">{ride.start_text}</div>
                            <div className="font-medium text-sm mt-1.5">{ride.end_text}</div>
                          </div>
                        </div>

                        <div className="text-sm text-gray-600 ml-6">
                          {new Date(ride.depart_at).toLocaleString()} • {ride.seats_available} seats available
                        </div>

                        {/* INCOMING REQUESTS */}
                        {rideRequests.length > 0 && (
                          <div className="mt-4 ml-6 space-y-2">
                            <h4 className="text-sm font-semibold text-gray-700">Ride Requests:</h4>
                            {rideRequests.map(req => (
                              <div key={req.request_id} className={`p-3 rounded-lg border-2 ${
                                req.status === 'pending' ? 'border-yellow-200 bg-yellow-50' : 'border-green-200 bg-green-50'
                              }`}>
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <User className="w-4 h-4 text-gray-600" />
                                      <span className="font-medium text-sm">{req.rider_name}</span>
                                      {req.status === 'approved' && (
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {req.seats_requested} seat{req.seats_requested > 1 ? 's' : ''} • {req.rider_gender === 'female' ? '👩' : '👨'}
                                    </div>
                                    {req.start_text && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        📍 {req.start_text} → {req.end_text}
                                      </div>
                                    )}
                                  </div>
                                  {req.status === 'pending' && (
                                    <div className="flex gap-2 ml-2">
                                      <button
                                        onClick={() => openRespondModal('accept', req.ride_id, req.rider_id)}
                                        className="px-3 py-1 bg-emerald-500 text-white rounded text-xs hover:bg-emerald-600">
                                        Accept
                                      </button>
                                      <button
                                        onClick={() => openRespondModal('decline', req.ride_id, req.rider_id)}
                                        className="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600">
                                        Decline
                                      </button>
                                    </div>
                                  )}
                                  {req.status === 'approved' && (
                                    <div className="text-xs text-green-700 font-medium">✓ Approved</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="text-right ml-4">
                        <div className="text-lg font-bold text-orange-600">Rs. {ride.price_per_seat}</div>
                        <div className="text-xs text-gray-500 mb-2">per seat</div>
                        <button 
                          onClick={() => openCancelModal(ride.id, 'driver')}
                          className="text-xs px-3 py-1.5 border border-red-500 text-red-600 rounded-lg hover:bg-red-50">
                          Cancel Ride
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* RIDES I'M TAKING */}
        {myRides.as_rider?.length > 0 && (
          <div>
            <h3 className="text-md font-semibold mb-3 text-emerald-700">
              Rides I'm Taking ({myRides.as_rider.length})
            </h3>
            <div className="space-y-3">
              {myRides.as_rider.map(ride => (
                <div key={ride.request_id} className="bg-white rounded-xl border-2 border-emerald-200 p-5">
                  <div className="flex justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-emerald-500 text-white rounded text-xs font-medium">
                          Taking
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          ride.status === 'approved' ? 'bg-green-100 text-green-700' :
                          ride.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          ride.status === 'declined' ? 'bg-red-100 text-red-700' :
                          ride.status === 'cancelled_by_driver' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {ride.status.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>

                      <div className="flex gap-2 mb-2">
                        <div className="flex flex-col items-center pt-1">
                          <MapPin className="w-4 h-4 text-emerald-500" />
                          <div className="w-px h-4 bg-gray-300"></div>
                          <MapPin className="w-4 h-4 text-red-500 fill-red-500" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">{ride.start_text}</div>
                          <div className="font-medium text-sm mt-1.5">{ride.end_text}</div>
                        </div>
                      </div>

                      <div className="text-sm text-gray-600 ml-6">
                        {new Date(ride.depart_at).toLocaleString()}
                      </div>

                      {ride.status === 'cancelled_by_driver' && (
                        <div className="mt-3 ml-6">
                          <button
                            onClick={() => setActiveView('find')}
                            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm">
                            Find Another Ride →
                          </button>
                        </div>
                      )}

                      {ride.status === 'cancelled_by_rider' && (
                        <div className="mt-3 ml-6">
                          <button
                            onClick={() => setActiveView('find')}
                            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm">
                            Find Another Ride →
                          </button>
                        </div>
                      )}

                      {ride.status === 'approved' && (
                        <div className="mt-3 ml-6 p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4 text-emerald-600" />
                            <span className="font-medium">Contact will be shared soon</span>
                          </div>
                        </div>
                      )}

                      {ride.status === 'pending' && (
                        <div className="mt-3 ml-6 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="flex items-center gap-2 text-sm text-yellow-700">
                            <AlertTriangle className="w-4 h-4" />
                            <span>Waiting for driver approval</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-emerald-600">Rs. {ride.price_per_seat}</div>
                      <div className="text-xs text-gray-500 mb-2">per seat</div>
                      {(ride.status === 'pending' || ride.status === 'approved') && (
                        <button 
                          onClick={() => openCancelModal(ride.ride_id, 'rider')}
                          className="text-xs px-3 py-1.5 border border-red-500 text-red-600 rounded-lg hover:bg-red-50">
                          Cancel Request
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
)}
      {/* SKOOLPOOL VIEW */}
      {activeView.startsWith('skoolpool') && (
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border-2 border-indigo-200 p-8 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <GraduationCap className="w-8 h-8 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">SkoolPool</h2>
            </div>
            <p className="text-gray-600">Safe, reliable school commute for your children</p>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">⚠️ SkoolPool feature is coming soon! Backend integration in progress.</p>
            </div>
          </div>

          {/* Toggle between Request and Offer */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => setActiveView('skoolpool-request')}
              className={`flex-1 py-3 rounded-lg font-medium transition ${activeView === 'skoolpool-request'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-white border-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50'
                }`}>
              <Search className="w-4 h-4 inline mr-2" />
              Request School Ride
            </button>
            <button
              onClick={() => setActiveView('skoolpool-offer')}
              className={`flex-1 py-3 rounded-lg font-medium transition ${activeView === 'skoolpool-offer'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white border-2 border-orange-200 text-orange-700 hover:bg-orange-50'
                }`}>
              <Plus className="w-4 h-4 inline mr-2" />
              Offer School Ride
            </button>
          </div>

          {/* REQUEST SCHOOL RIDE */}
          {activeView === 'skoolpool-request' && (
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Child</label>
                <select className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white">
                  {familyProfiles.filter(p => p.type === 'child').map(child => (
                    <option key={child.id}>{child.name} (Age {child.age} - {child.school})</option>
                  ))}
                  <option>+ Add New Child</option>
                </select>
              </div>

              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-5 h-5 text-indigo-600" />
                  <span className="font-medium text-sm">School Details</span>
                </div>
                <div className="text-sm text-gray-700">
                  <div className="font-medium">Green Valley School</div>
                  <div className="text-xs text-gray-500 mt-1">Street 5, F-7/3, Islamabad</div>
                </div>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <Repeat className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-sm">Request recurring school rides</span>
                </div>
                <div className="flex gap-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                    <button
                      key={day}
                      onClick={() => toggleRecurringDay(day)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${recurringDays.includes(day)
                          ? 'bg-indigo-500 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Time Needed</label>
                  <input type="time" defaultValue="07:30" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seats Needed</label>
                  <select className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white">
                    <option>1</option><option>2</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Willing to Pay per Seat (PKR)</label>
                <input type="number" placeholder="200" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
                <p className="text-xs text-gray-500 mt-1">Your budget per ride</p>
              </div>

              <button 
                disabled
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-4 rounded-lg font-semibold text-lg opacity-50 cursor-not-allowed">
                <Search className="w-5 h-5 inline mr-2" />
                Coming Soon
              </button>
            </div>
          )}

          {/* OFFER SCHOOL RIDE */}
          {activeView === 'skoolpool-offer' && (
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-gray-700">You're offering to drive children to school. Parents will request rides from you.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
                <select className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-white">
                  <option>Green Valley School</option>
                  <option>City Public School</option>
                  <option>+ Add School</option>
                </select>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <Repeat className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-sm">School runs are recurring by default</span>
                </div>
                <div className="flex gap-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                    <button
                      key={day}
                      onClick={() => toggleRecurringDay(day)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${recurringDays.includes(day)
                          ? 'bg-orange-500 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Time</label>
                  <input type="time" defaultValue="07:30" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Available Seats</label>
                  <select className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-white">
                    <option>1</option><option>2</option><option>3</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price per Seat (PKR)</label>
                <input type="number" placeholder="150" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                <p className="text-xs text-gray-500 mt-1">Suggested: Rs. 150-300 per day</p>
              </div>

              <button 
                disabled
                className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white py-4 rounded-lg font-semibold text-lg opacity-50 cursor-not-allowed">
                <Send className="w-5 h-5 inline mr-2" />
                Coming Soon
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}